import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../../firebase";
import PageWrapper from "../../../components/PageWrapper";
import "./css/VehicleWeaponDetail.css";

import WeaponDetailContent from "./WeaponDetailContent";
import { normalizeWeapon } from "./normalizeWeapon";

/* ---------- Utils ---------- */
function toYouTubeEmbed(u = "") {
  if (!/youtu\.?be/.test(u)) return null;
  try {
    const normalized = u.replace("youtu.be/", "www.youtube.com/watch?v=");
    const url = new URL(normalized);
    const v = url.searchParams.get("v");
    if (v) return `https://www.youtube.com/embed/${v}`;
    const last = url.pathname.split("/").filter(Boolean).pop();
    return last ? `https://www.youtube.com/embed/${last}` : null;
  } catch {
    return null;
  }
}

/* ---------- Component ---------- */
export default function WeaponDetail() {
  const { id } = useParams();
  const location = useLocation();

  // state
  const [w, setW] = useState(undefined); // undefined = loading, null = not found
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const lightboxRef = useRef(null);

  const basePath = useMemo(() => ["weapons", id], [id]);
  const bookmarkKey = useMemo(() => `weapons:${id}`, [id]);

  // reactions (same pattern as vehicles)
  const [selectedReactionFromBar, setSelectedReactionFromBar] = useState(null);

  /* ---------- Firestore ---------- */
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "weapons", id), (snap) => {
      if (!snap.exists()) {
        setW(null);
        return;
      }
      setW({ id: snap.id, ...normalizeWeapon(snap.data()) });
    });
    return () => unsub();
  }, [id]);

  /* ---------- Derived ---------- */
  const yt = useMemo(
    () => (w?.videoUrl ? toYouTubeEmbed(w.videoUrl) : null),
    [w?.videoUrl]
  );

  /* ---------- Share ---------- */
  const onShare = useCallback(async () => {
    const url = window.location.origin + location.pathname + location.hash;
    try {
      if (navigator.share) {
        await navigator.share({ title: w?.name ?? "Weapon", url });
        return;
      }
      await navigator.clipboard.writeText(url);
    } catch { }
  }, [w?.name, location.pathname, location.hash]);

  /* ---------- Lightbox ---------- */
  const openLightbox = useCallback((index) => {
    setLightbox({ open: true, index });
  }, []);

  const closeLightbox = useCallback(() => {
    setLightbox({ open: false, index: 0 });
  }, []);

  const nextLight = useCallback(
    (e) => {
      e?.stopPropagation?.();
      setLightbox((s) => {
        const len = w?.gallery?.length ?? 0;
        return { open: true, index: len ? (s.index + 1) % len : 0 };
      });
    },
    [w?.gallery?.length]
  );

  const prevLight = useCallback(
    (e) => {
      e?.stopPropagation?.();
      setLightbox((s) => {
        const len = w?.gallery?.length ?? 0;
        return { open: true, index: len ? (s.index - 1 + len) % len : 0 };
      });
    },
    [w?.gallery?.length]
  );

  useEffect(() => {
    if (!lightbox.open) return;
    function onKey(e) {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextLight();
      if (e.key === "ArrowLeft") prevLight();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox.open, nextLight, prevLight, closeLightbox]);

  /* ---------- Loading / 404 ---------- */
  if (w === undefined) {
    return (
      <PageWrapper>
        <div className="vw-wrap">
          <div className="vw-skel-card" aria-busy="true">
            <div className="vw-skel-hero" />
            <div className="vw-skel-title" />
            <div className="vw-skel-row" />
            <div className="vw-skel-section" />
          </div>
        </div>
      </PageWrapper>
    );
  }

  if (w === null) {
    return (
      <PageWrapper>
        <div className="vw-wrap">
          <div className="vw-empty-card" role="alert">
            <div className="vw-empty-title">We can’t find that weapon</div>
            <p className="vw-empty-sub">
              It may have been removed or the link is incorrect.
            </p>
            <Link
              to="/community/vehicles_weapons?tab=weapons"
              className="vw-btn vw-btn--primary"
            >
              ← Back to Weapons
            </Link>
          </div>
        </div>
      </PageWrapper>
    );
  }

  /* ---------- Render ---------- */
  return (
    <PageWrapper>
      <div className="vw-wrap">
        {/* Top bar */}
        <div className="vw-toprow">
          <Link
            to="/vehicles-weapons?tab=weapons"
            className="vw-back"
            aria-label="Back to weapons list"
          >
            ← All Weapons
          </Link>
          <div className="vw-actions">
            {w.videoUrl && !yt && (
              <a
                className="vw-action"
                href={w.videoUrl}
                target="_blank"
                rel="noreferrer"
              >
                ▶ Open video
              </a>
            )}
            <button className="vw-action" onClick={onShare}>
              ⤴ Share
            </button>
          </div>
        </div>

        {/* MAIN CONTENT */}
        <WeaponDetailContent
          w={w}
          entityId={id}
          basePath={basePath}
          bookmarkKey={bookmarkKey}
          yt={yt}
          openLightbox={openLightbox}
          selectedReactionFromBar={selectedReactionFromBar}
          setSelectedReactionFromBar={setSelectedReactionFromBar}
        />
      </div>

      {/* Lightbox */}
      {lightbox.open && Array.isArray(w.gallery) && w.gallery.length > 0 && (
        <div
          ref={lightboxRef}
          className="vw-lightbox"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label="Image viewer"
        >
          <button
            className="vw-lightbox-close"
            onClick={closeLightbox}
            aria-label="Close"
          >
            ✕
          </button>
          <button
            className="vw-lightbox-nav left"
            onClick={prevLight}
            aria-label="Previous"
          >
            ‹
          </button>
          <img
            className="vw-lightbox-img"
            src={w.gallery[lightbox.index]}
            alt={`Image ${lightbox.index + 1}`}
          />
          <button
            className="vw-lightbox-nav right"
            onClick={nextLight}
            aria-label="Next"
          >
            ›
          </button>
        </div>
      )}
    </PageWrapper>
  );
}
